import type { AppSubStoreArgs } from "@/store/types";
import { type SyncUpdateValue, type OptimisticSyncUpdate, SyncModelKind } from "@/store/sync/types";
import { DataImportKind, type DataImportSyncModelData } from "@/store/data-imports/types";
import { BaseSyncModelStore } from "@/store/sync/BaseSyncModelStore";
import { DataImportObservable } from "@/store/data-imports/DataImportObservable";
import {
  computed,
  makeObservable,
  observable,
  runInAction,
  onBecomeObserved,
  action,
  override,
  onBecomeUnobserved,
} from "mobx";
import { Maybe } from "@/domains/common/types";
import { liveQuery, Subscription } from "dexie";
import Dexie from "dexie";
import { AppStore } from "@/store";
import { DataImportIndexes } from "@/store/data-imports/DataImportIndexes";

type DataImportItemTuple = [started_at: string, model_id: string];

export class AppStoreDataImportsStore extends BaseSyncModelStore<
  DataImportObservable,
  DataImportSyncModelData
> {
  liveQuerySubscription?: Subscription;
  subscribedItems: DataImportItemTuple[] = [];

  constructor(injectedDeps: AppSubStoreArgs) {
    super({ modelKind: SyncModelKind.DataImport, ...injectedDeps });

    makeObservable<this, "getLatestImports" | "getItems">(this, {
      computeIndexes: override,
      createSyncModel: false,

      liveQuerySubscription: observable,
      subscribedItems: observable,
      unsubscribeLiveQuery: action,
      subscribeLiveQuery: action,

      latestImport: computed,
      length: computed,
      getLatestImports: false,
      isImportInProgress: computed,
      isMigrationInProgress: computed,
      migrations: computed,
      isMigrationStarted: computed,
      getItems: false,
    });

    onBecomeObserved(this, "subscribedItems", () => this.subscribeLiveQuery());
    onBecomeUnobserved(this, "subscribedItems", () => this.unsubscribeLiveQuery());
  }

  createSyncModel(data: SyncUpdateValue<DataImportSyncModelData>): DataImportObservable {
    return new DataImportObservable({
      id: data.model_id,
      data,
      store: this.store,
    });
  }

  subscribeLiveQuery() {
    this.liveQuerySubscription?.unsubscribe();
    this.liveQuerySubscription = liveQuery(() => {
      return this.localTable
        .where("[started_at+model_id]")
        .above(["\0", Dexie.minKey])
        .reverse()
        .keys();
    }).subscribe({
      next: ids => {
        runInAction(() => {
          this.subscribedItems = ids as unknown as DataImportItemTuple[];
        });
      },
    });
  }

  unsubscribeLiveQuery() {
    this.liveQuerySubscription?.unsubscribe();
  }

  get length(): number {
    return this.subscribedItems.length;
  }

  get latestImport(): Maybe<DataImportObservable> {
    const latestOne = this.getLatestImports(1);
    return latestOne[0];
  }

  get isImportInProgress(): boolean {
    /**
     * If the `ImportProcessStartedModal` is open (or an import started within the last minute),
     * we want to make -sure- we are showing the banner.
     *
     * This gives some buffer time for the import to sync to the client.
     *
     * In an ideal world, all data imports would be created via sync operations + optimistic updates,
     * but for the v1 -> v2 transition, we are using this workaround.
     */
    if (
      this.store.accountTransition.importProcessStartedModalIsOpen ||
      this.store.accountTransition.importProcessStartedRecently
    ) {
      return true;
    }

    return this.latestImport?.isRunning ?? false;
  }

  getLatestImports(count: number): DataImportObservable[] {
    return this.getItems(count);
  }

  get isMigrationStarted(): boolean {
    return this.migrations.length > 0;
  }

  get isMigrationInProgress(): boolean {
    return this.migrations.some(item => item?.isRunning);
  }

  get migrations(): DataImportObservable[] {
    return this.getItems().filter(
      item => item?.kind === DataImportKind.MemClassicBatchAccountUpgradeV1
    );
  }

  public computeIndexes({
    remoteData,
    optimisticUpdates,
  }: {
    store: AppStore;
    remoteData: Maybe<SyncUpdateValue<DataImportSyncModelData>>;
    optimisticUpdates: OptimisticSyncUpdate<DataImportSyncModelData>[];
  }): Record<string, unknown> {
    return new DataImportIndexes({ remoteData, optimisticUpdates }).indexes;
  }

  private getItems(count?: number): DataImportObservable[] {
    const slicedItems = count ? this.subscribedItems.slice(0, count) : this.subscribedItems;

    return slicedItems.map(([_startedAt, id]) => this.get(id)).filter(e => !!e);
  }
}
